import React, { useState } from 'react';
import { SavedQuote } from '../App';
import { ArrowLeftIcon, PrinterIcon, DownloadIcon, BuildingIcon, UserIcon, CheckCircleIcon, ShoppingCartIcon, XIcon, PackageIcon, ArrowRightIcon, EditIcon, TrashIcon } from 'lucide-react';
interface QuotePrintViewProps {
  quote: SavedQuote;
  onBack: () => void;
  onDownload?: () => void;
  onEdit?: () => void;
  onDelete?: () => void;
}
type ViewMode = 'dealer' | 'customer';
export function QuotePrintView({
  quote,
  onBack,
  onDownload,
  onEdit,
  onDelete
}: QuotePrintViewProps) {
  const [viewMode, setViewMode] = useState<ViewMode>('dealer');
  const [showConvertModal, setShowConvertModal] = useState(false);
  const subtotal = quote.parts.reduce((sum, part) => sum + part.quantity * part.unitPrice, 0);
  // Dealer pricing calculations
  const dealerDiscount = 10; // 10%
  const dealerMargin = 15; // 15%
  const dealerPrice = subtotal * (1 - dealerDiscount / 100);
  const customerPrice = dealerPrice * (1 + dealerMargin / 100);
  const transportTotal = quote.transport;
  const insuranceTotal = quote.insurance;
  // Calculate total based on view mode
  const pricingBase = quote.quoteType === 'dealer-customer' ? viewMode === 'customer' ? customerPrice : dealerPrice : subtotal;
  const total = pricingBase + transportTotal + insuranceTotal;
  const handlePrint = () => {
    window.print();
  };
  const handleConvertToOrder = () => {
    setShowConvertModal(true);
  };
  const handleConfirmConvert = () => {
    // TODO: Implement actual order conversion logic
    alert(`Quote ${quote.id} converted to official order!\n\nOrder ID: ORD-${quote.id}\nStatus: Processing\n\nRedirecting to operations workflow...`);
    setShowConvertModal(false);
    // Here you would typically navigate to the orders/operations view
  };
  const handleDelete = () => {
    if (confirm('¿Está seguro de que desea eliminar esta cotización?')) {
      onDelete?.();
    }
  };
  return <div className="w-full min-h-screen bg-gray-100" data-id="element-289">
      {/* Header - Hidden on print */}
      <div className="bg-gradient-to-r from-[#0a1e3d] via-[#0d3d5c] to-[#1a7a8a] border-b-2 border-blue-600/30 shadow-xl print:hidden" data-id="element-290">
        <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-291">
          <div className="flex items-center justify-between" data-id="element-292">
            {/* Left - Back button */}
            <div className="flex-1" data-id="element-293">
              <button onClick={onBack} className="flex items-center gap-2 px-4 py-2 text-white bg-white/10 rounded-lg hover:bg-white/20 transition-all" data-id="element-294">
                <ArrowLeftIcon className="w-5 h-5" data-id="element-295" />
                Volver a la lista
              </button>
            </div>

            {/* Center - Action buttons */}
            <div className="flex items-center justify-center gap-2 flex-1" data-id="element-296">
              {onDownload && <button onClick={onDownload} className="flex items-center gap-2 px-4 py-2 text-white bg-white/10 rounded-lg hover:bg-white/20 transition-all" title="Descargar" data-id="element-297">
                  <DownloadIcon className="w-5 h-5" data-id="element-298" />
                  Download
                </button>}
              {onEdit && <button onClick={onEdit} className="flex items-center gap-2 px-4 py-2 text-white bg-white/10 rounded-lg hover:bg-white/20 transition-all" title="Editar" data-id="element-299">
                  <EditIcon className="w-5 h-5" data-id="element-300" />
                  Edit
                </button>}
              {onDelete && <button onClick={handleDelete} className="flex items-center gap-2 px-4 py-2 text-white bg-white/10 rounded-lg hover:bg-white/20 transition-all" title="Eliminar" data-id="element-301">
                  <TrashIcon className="w-5 h-5" data-id="element-302" />
                  Delete
                </button>}
            </div>

            {/* Right - Empty space for balance */}
            <div className="flex-1" data-id="element-303"></div>
          </div>
        </div>
      </div>

      {/* Controls Bar - Below Header, Above Document */}
      <div className="bg-gradient-to-b from-gray-50 to-white border-b border-gray-200 print:hidden shadow-sm" data-id="element-304"></div>

      {/* Print Content */}
      <div className="max-w-4xl mx-auto p-8 print:p-0 pb-32" data-id="element-305">
        {/* View Mode Toggle - Centered above document */}
        {quote.quoteType === 'dealer-customer' && <div className="flex justify-center mb-6 print:hidden" data-id="element-306">
            <div className="flex gap-2 bg-white p-1.5 rounded-xl shadow-inner border border-gray-200" data-id="element-307">
              <button onClick={() => setViewMode('dealer')} className={`relative flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold transition-all ${viewMode === 'dealer' ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 shadow-lg shadow-blue-200/40' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-308">
                <BuildingIcon className="w-4 h-4" data-id="element-309" />
                <span className="text-sm" data-id="element-310">Dealer</span>
                {viewMode === 'dealer' && <CheckCircleIcon className="w-4 h-4 ml-1" data-id="element-311" />}
              </button>
              <button onClick={() => setViewMode('customer')} className={`relative flex items-center gap-2 px-5 py-2.5 rounded-lg font-bold transition-all ${viewMode === 'customer' ? 'bg-gradient-to-r from-blue-100 to-blue-200 text-blue-700 shadow-lg shadow-blue-200/40' : 'text-gray-600 hover:bg-gray-100'}`} data-id="element-312">
                <UserIcon className="w-4 h-4" data-id="element-313" />
                <span className="text-sm" data-id="element-314">Customer</span>
                {viewMode === 'customer' && <CheckCircleIcon className="w-4 h-4 ml-1" data-id="element-315" />}
              </button>
            </div>
          </div>}

        <div className="bg-white rounded-xl shadow-lg print:shadow-none print:rounded-none" data-id="element-316">
          <div className="p-12" data-id="element-317">
            {/* Header */}
            <div className="text-center mb-8 pb-6 border-b-2 border-gray-300" data-id="element-318">
              <h1 className="text-4xl font-bold text-gray-900 mb-3" data-id="element-319">
                COTIZACIÓN
              </h1>
              <h2 className="text-2xl font-semibold text-gray-700 mb-4" data-id="element-320">
                {quote.name || 'Sin nombre'}
              </h2>
              <div className="flex justify-center gap-8 text-sm text-gray-600" data-id="element-321">
                <div data-id="element-322">
                  <span className="font-semibold" data-id="element-323">Fecha de Creación:</span>{' '}
                  {new Date(quote.createdAt).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                </div>
                {quote.finalizedAt && <div data-id="element-324">
                    <span className="font-semibold" data-id="element-325">Finalizada:</span>{' '}
                    {new Date(quote.finalizedAt).toLocaleDateString('es-ES', {
                  year: 'numeric',
                  month: 'long',
                  day: 'numeric'
                })}
                  </div>}
              </div>
              {quote.quoteType === 'dealer-customer' && <div className="mt-4" data-id="element-326">
                  <span className={`inline-flex items-center px-4 py-2 rounded-full font-semibold ${viewMode === 'dealer' ? 'bg-slate-100 text-slate-800 border border-slate-300' : 'bg-green-100 text-green-800 border border-green-300'}`} data-id="element-327">
                    {viewMode === 'dealer' ? 'Vista Dealer' : 'Vista Customer'}
                  </span>
                </div>}
            </div>

            {/* Pivots Section */}
            {quote.pivots.length > 0 && <div className="mb-8" data-id="element-328">
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-329">
                  Pivots Seleccionados
                </h3>
                <table className="w-full" data-id="element-330">
                  <thead data-id="element-331">
                    <tr className="bg-gray-100 border-b-2 border-gray-300" data-id="element-332">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700" data-id="element-333">
                        Nombre
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700" data-id="element-334">
                        Modelo
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700" data-id="element-335">
                        Cantidad
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700" data-id="element-336">
                        Especificaciones
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-337">
                    {quote.pivots.map(pivot => <tr key={pivot.id} className="border-b border-gray-200" data-id="element-338">
                        <td className="px-4 py-3 text-sm text-gray-900 font-semibold" data-id="element-339">
                          {pivot.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-700" data-id="element-340">
                          {pivot.model}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-center font-semibold" data-id="element-341">
                          {pivot.quantity}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600" data-id="element-342">
                          {pivot.length} • {pivot.area} • {pivot.flow}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}

            {/* Parts Section */}
            {quote.parts.length > 0 && <div className="mb-8" data-id="element-343">
                <h3 className="text-xl font-bold text-gray-900 mb-4 pb-2 border-b border-gray-200" data-id="element-344">
                  Detalles de Piezas
                </h3>
                <table className="w-full" data-id="element-345">
                  <thead data-id="element-346">
                    <tr className="bg-gray-100 border-b-2 border-gray-300" data-id="element-347">
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700" data-id="element-348">
                        Pieza
                      </th>
                      <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700" data-id="element-349">
                        No. Parte
                      </th>
                      <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700" data-id="element-350">
                        Cant.
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700" data-id="element-351">
                        Precio Unit.
                      </th>
                      <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700" data-id="element-352">
                        Total
                      </th>
                    </tr>
                  </thead>
                  <tbody data-id="element-353">
                    {quote.parts.map(part => <tr key={part.id} className="border-b border-gray-200" data-id="element-354">
                        <td className="px-4 py-3 text-sm text-gray-900" data-id="element-355">
                          {part.name}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600" data-id="element-356">
                          {part.partNumber}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-center" data-id="element-357">
                          {part.quantity}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-900 text-right" data-id="element-358">
                          {quote.currency} ${part.unitPrice.toFixed(2)}
                        </td>
                        <td className="px-4 py-3 text-sm font-semibold text-gray-900 text-right" data-id="element-359">
                          {quote.currency} $
                          {(part.quantity * part.unitPrice).toFixed(2)}
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>}

            {/* Totals */}
            <div className="bg-gray-50 rounded-lg p-6 border-2 border-gray-200" data-id="element-360">
              <div className="space-y-3" data-id="element-361">
                <div className="flex justify-between text-gray-700" data-id="element-362">
                  <span className="font-medium" data-id="element-363">Subtotal:</span>
                  <span className="font-semibold text-lg" data-id="element-364">
                    {quote.currency} ${subtotal.toFixed(2)}
                  </span>
                </div>

                {/* Dealer-Customer specific pricing */}
                {quote.quoteType === 'dealer-customer' && <>
                    {viewMode === 'dealer' && <>
                        <div className="flex justify-between text-green-700" data-id="element-365">
                          <span className="font-medium" data-id="element-366">
                            Dealer Discount ({dealerDiscount}%):
                          </span>
                          <span className="font-semibold" data-id="element-367">
                            - {quote.currency} $
                            {(subtotal * (dealerDiscount / 100)).toFixed(2)}
                          </span>
                        </div>
                        <div className="pt-3 border-t border-gray-300" data-id="element-368">
                          <div className="flex justify-between text-xl" data-id="element-369">
                            <span className="font-bold text-gray-900" data-id="element-370">
                              Dealer Price:
                            </span>
                            <span className="font-bold text-gray-900" data-id="element-371">
                              {quote.currency} ${dealerPrice.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </>}

                    {viewMode === 'customer' && <>
                        <div className="flex justify-between text-blue-700" data-id="element-372">
                          <span className="font-medium" data-id="element-373">
                            Dealer Margin ({dealerMargin}%):
                          </span>
                          <span className="font-semibold" data-id="element-374">
                            + {quote.currency} $
                            {(dealerPrice * (dealerMargin / 100)).toFixed(2)}
                          </span>
                        </div>
                        <div className="pt-3 border-t border-gray-300" data-id="element-375">
                          <div className="flex justify-between text-xl" data-id="element-376">
                            <span className="font-bold text-gray-900" data-id="element-377">
                              Customer Price:
                            </span>
                            <span className="font-bold text-gray-900" data-id="element-378">
                              {quote.currency} ${customerPrice.toFixed(2)}
                            </span>
                          </div>
                        </div>
                      </>}
                  </>}

                <div className="flex justify-between text-gray-700" data-id="element-379">
                  <span className="font-medium" data-id="element-380">Transporte:</span>
                  <span className="font-semibold text-lg" data-id="element-381">
                    {quote.currency} ${transportTotal.toFixed(2)}
                  </span>
                </div>
                <div className="flex justify-between text-gray-700" data-id="element-382">
                  <span className="font-medium" data-id="element-383">Seguro:</span>
                  <span className="font-semibold text-lg" data-id="element-384">
                    {quote.currency} ${insuranceTotal.toFixed(2)}
                  </span>
                </div>
                <div className="pt-4 border-t-2 border-gray-300" data-id="element-385">
                  <div className="flex justify-between text-2xl font-bold text-gray-900" data-id="element-386">
                    <span data-id="element-387">Total:</span>
                    <span data-id="element-388">
                      {quote.currency} ${total.toFixed(2)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="mt-8 pt-6 border-t border-gray-200 text-center text-sm text-gray-500" data-id="element-389">
              <p data-id="element-390">
                Esta cotización es válida por 30 días desde la fecha de emisión.
              </p>
              <p className="mt-2" data-id="element-391">
                Para más información, por favor contacte con nuestro equipo de
                ventas.
              </p>
            </div>
          </div>
        </div>

        {/* Fixed Bottom Bar - Hidden on print */}
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-gray-200 shadow-2xl print:hidden z-50" data-id="element-392">
          <div className="max-w-7xl mx-auto px-6 py-4" data-id="element-393">
            <div className="flex items-center justify-between gap-6" data-id="element-394">
              {/* Left - Total Amount */}
              <div className="flex items-center gap-4" data-id="element-395">
                <div data-id="element-396">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide" data-id="element-397">
                    Total (CIF)
                  </p>
                  <p className="text-2xl font-bold text-gray-900" data-id="element-398">
                    {quote.currency} ${total.toFixed(2)}
                  </p>
                </div>
              </div>

              {/* Right - Action Buttons */}
              <div className="flex items-center gap-3" data-id="element-399">
                {/* Download Button - Secondary */}
                {onDownload && <button onClick={onDownload} className="flex items-center gap-2 px-5 py-3 bg-white text-gray-700 rounded-lg hover:bg-blue-50 hover:text-blue-600 transition-all font-medium border-2 border-gray-300 hover:border-blue-500 shadow-sm" data-id="element-400">
                    <DownloadIcon className="w-5 h-5" data-id="element-401" />
                    <span data-id="element-402">Download Quote</span>
                  </button>}

                {/* Convert to Order Button - PRIMARY CTA */}
                <button onClick={handleConvertToOrder} className="relative flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all font-bold shadow-xl hover:shadow-2xl hover:scale-105 text-lg group overflow-hidden" data-id="element-403">
                  {/* Animated background effect */}
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-blue-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" data-id="element-404"></div>

                  {/* Content */}
                  <div className="relative flex items-center gap-3" data-id="element-405">
                    <ShoppingCartIcon className="w-6 h-6" data-id="element-406" />
                    <span data-id="element-407">Place Order</span>
                    <ArrowRightIcon className="w-5 h-5 group-hover:translate-x-1 transition-transform" data-id="element-408" />
                  </div>

                  {/* Pulse animation */}
                  <div className="absolute inset-0 rounded-xl bg-blue-400 opacity-0 group-hover:opacity-20 animate-pulse" data-id="element-409"></div>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Convert to Order Confirmation Modal */}
      {showConvertModal && <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" data-id="element-410">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full" data-id="element-411">
            <div className="bg-gradient-to-r from-blue-600 to-blue-700 p-6 relative rounded-t-2xl" data-id="element-412">
              <button onClick={() => setShowConvertModal(false)} className="absolute top-4 right-4 p-2 text-white hover:bg-white/20 rounded-lg transition-colors" data-id="element-413">
                <XIcon className="w-5 h-5" data-id="element-414" />
              </button>
              <div className="flex items-center gap-3" data-id="element-415">
                <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center" data-id="element-416">
                  <PackageIcon className="w-6 h-6 text-white" data-id="element-417" />
                </div>
                <div data-id="element-418">
                  <h2 className="text-2xl font-bold text-white" data-id="element-419">
                    Convert to Order
                  </h2>
                  <p className="text-blue-100 text-sm" data-id="element-420">
                    Start operations workflow
                  </p>
                </div>
              </div>
            </div>
            <div className="p-6 space-y-4" data-id="element-421">
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-422">
                <p className="text-sm text-gray-700 mb-3" data-id="element-423">
                  <span className="font-semibold" data-id="element-424">Quote:</span>{' '}
                  {quote.name ? `${quote.name} (${quote.id})` : quote.id}
                </p>
                <p className="text-sm text-gray-700 mb-3" data-id="element-425">
                  <span className="font-semibold" data-id="element-426">Total Amount:</span>{' '}
                  {quote.currency} ${total.toFixed(2)}
                </p>
                <p className="text-sm text-gray-700" data-id="element-427">
                  <span className="font-semibold" data-id="element-428">Machines:</span>{' '}
                  {quote.pivots.reduce((sum, pivot) => sum + pivot.quantity, 0)}{' '}
                  {quote.pivots.reduce((sum, pivot) => sum + pivot.quantity, 0) === 1 ? 'unit' : 'units'}
                </p>
              </div>

              <div className="bg-amber-50 border border-amber-200 rounded-lg p-4" data-id="element-429">
                <p className="text-sm text-amber-800" data-id="element-430">
                  <span className="font-semibold" data-id="element-431">⚠️ Important:</span>{' '}
                  Converting this quote to an official order will:
                </p>
                <ul className="mt-2 space-y-1 text-sm text-amber-700 ml-4" data-id="element-432">
                  <li data-id="element-433">• Create a new order in the system</li>
                  <li data-id="element-434">• Start the operations workflow</li>
                  <li data-id="element-435">• Lock the pricing and specifications</li>
                  <li data-id="element-436">• Notify relevant departments</li>
                </ul>
              </div>

              <div className="flex gap-3" data-id="element-437">
                <button onClick={handleConfirmConvert} className="flex-1 flex items-center justify-center gap-2 px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all font-semibold shadow-lg" data-id="element-438">
                  <CheckCircleIcon className="w-5 h-5" data-id="element-439" />
                  Confirm Order
                </button>
                <button onClick={() => setShowConvertModal(false)} className="px-6 py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors font-semibold" data-id="element-440">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}
    </div>;
}